// SkipDriverMapPage — /skip/drivers/map
// Real-time driver location map for skip depot staff, with route-history replay.

import { useState } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { DriverMapView } from '../../../components/ui/DriverMapView/DriverMapView';
import {
  useLatestDriverLocations,
  useLatestStaffLocations,
  useDriverLocationHistory,
} from '../hooks/useDriverLocations';
import type { MapLocationEntry } from '../../../api/driverLocations';
import { useMapCentre } from '../../../hooks/useMapCentre';
import { useActiveDrivers } from '../hooks/useActiveDrivers';
import { ReplayControls } from './ReplayControls';
import styles from './SkipDriverMapPage.module.css';

export function SkipDriverMapPage() {
  const { data, isLoading, dataUpdatedAt } = useLatestDriverLocations();
  const { data: staffData } = useLatestStaffLocations();
  const { data: activeDrivers = [] } = useActiveDrivers();
  const centre = useMapCentre();
  const driverLocations = data?.locations ?? [];
  const staffLocations = staffData?.locations ?? [];
  const mergedLocations: MapLocationEntry[] = [
    ...driverLocations.map((l) => ({ type: 'driver' as const, ...l })),
    ...staffLocations.map((l) => ({ type: 'staff' as const, ...l })),
  ];
  const updatedAt = dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null;

  // Replay state
  const [replayDriverId, setReplayDriverId] = useState<string | null>(null);
  const [replayDate, setReplayDate] = useState<string | null>(null);
  const isReplaying = !!replayDriverId && !!replayDate;

  const { data: historyData, isLoading: historyLoading } = useDriverLocationHistory(
    replayDriverId ?? '',
    replayDate ?? '',
  );

  // Driver name for replay subtitle — check active drivers list first,
  // fall back to live locations if the driver happens to be online.
  const replayDriverName =
    activeDrivers.find((d) => d.enc_id === replayDriverId)?.driver_name ??
    driverLocations.find((l) => l.enc_id === replayDriverId)?.driver_name ??
    'driver';

  const historyPoints = historyData?.history ?? [];

  function handleReplay(encDriverId: string, date: string) {
    setReplayDriverId(encDriverId);
    setReplayDate(date);
  }

  function handleClearReplay() {
    setReplayDriverId(null);
    setReplayDate(null);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Driver Map"
        subtitle={
          isReplaying
            ? `Showing route for ${replayDriverName} on ${replayDate}`
            : 'Live driver locations — updates every 30 seconds'
        }
      />
      <Card flush>
        <ReplayControls
          availableDrivers={activeDrivers}
          onReplay={handleReplay}
          onClearReplay={handleClearReplay}
          isReplaying={isReplaying}
        />
        {isLoading || historyLoading ? (
          <div className={styles.loader}>
            <Spinner size="sm" label="Loading…" />
          </div>
        ) : (
          <DriverMapView
            locations={isReplaying ? [] : mergedLocations}
            loading={false}
            updatedAt={isReplaying ? null : updatedAt}
            centre={centre}
            history={
              isReplaying
                ? { driverName: replayDriverName, points: historyPoints }
                : undefined
            }
          />
        )}
      </Card>
    </div>
  );
}
